import { useState, useEffect } from "react";
import {
  collection, addDoc, getDocs, doc, updateDoc,
  query, orderBy, where, serverTimestamp, limit
} from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";
import { C, F, Label, Btn, Spinner } from "./ui";

// ── Crear notificación (se llama desde cualquier módulo) ──────────────────────
export async function crearNotificacion({ usuario_id, usuario_email, tipo, titulo, mensaje, link = "" }) {
  try {
    await addDoc(collection(db, "notificaciones"), {
      usuario_id, usuario_email, tipo, titulo, mensaje, link,
      leida: false,
      creadoEn: serverTimestamp(),
    });
  } catch (e) {
    console.error("Error creando notificación:", e);
  }
}

// Tipos de notificación
export const NOTIF_TIPOS = {
  PEDIDO_CONFIRMADO:  { icon: "✓", color: "#60A5FA" },
  PEDIDO_PRODUCCION:  { icon: "⚙", color: "#F59E0B" },
  PEDIDO_EMBARCADO:   { icon: "🚢", color: "#8B5CF6" },
  PEDIDO_ADUANA:      { icon: "📋", color: "#EC4899" },
  PEDIDO_DEPOSITO:    { icon: "📦", color: "#3D7A5F" },
  PEDIDO_ENTREGADO:   { icon: "✅", color: "#555"    },
  COTIZACION_LISTA:   { icon: "💬", color: "#F59E0B" },
  CICLO_CERRANDO:     { icon: "⏰", color: "#EC4899" },
  GENERAL:            { icon: "·",  color: C.muted   },
};

// ── Campana de notificaciones (para el header) ────────────────────────────────
export function CampanaNotificaciones() {
  const { user } = useAuth();
  const [notifs, setNotifs]   = useState([]);
  const [open, setOpen]       = useState(false);
  

  const noLeidas = notifs.filter(n => !n.leida).length;

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      const snap = await getDocs(
        query(
          collection(db, "notificaciones"),
          where("usuario_id", "==", user.uid),
          orderBy("creadoEn", "desc"),
          limit(20)
        )
      );
      setNotifs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    load();
    const interval = setInterval(load, 30000); // Refresh cada 30s
    return () => clearInterval(interval);
  }, [user]);

  const marcarLeida = async (id) => {
    await updateDoc(doc(db, "notificaciones", id), { leida: true });
    setNotifs(prev => prev.map(n => n.id === id ? { ...n, leida: true } : n));
  };

  const marcarTodasLeidas = async () => {
    const promesas = notifs.filter(n => !n.leida).map(n =>
      updateDoc(doc(db, "notificaciones", n.id), { leida: true })
    );
    await Promise.all(promesas);
    setNotifs(prev => prev.map(n => ({ ...n, leida: true })));
  };

  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          position: "relative", background: "transparent", border: "none",
          cursor: "pointer", padding: "4px 8px", display: "flex", alignItems: "center",
        }}
      >
        <span style={{ fontFamily: F.mono, fontSize: 16, color: noLeidas > 0 ? C.white : C.muted }}>
          ◎
        </span>
        {noLeidas > 0 && (
          <span style={{
            position: "absolute", top: 0, right: 0,
            width: 14, height: 14, borderRadius: "50%",
            background: "#EC4899", color: C.white,
            fontFamily: F.mono, fontSize: 8,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>{noLeidas > 9 ? "9+" : noLeidas}</span>
        )}
      </button>

      {open && (
        <>
          {/* Backdrop */}
          <div style={{ position: "fixed", inset: 0, zIndex: 98 }} onClick={() => setOpen(false)} />

          {/* Panel */}
          <div style={{
            position: "absolute", top: "calc(100% + 8px)", right: 0,
            width: 340, background: C.s1, border: `0.5px solid ${C.border}`,
            zIndex: 99, maxHeight: 480, overflow: "hidden", display: "flex", flexDirection: "column",
            boxShadow: "0 8px 32px rgba(0,0,0,.6)",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", borderBottom: `0.5px solid ${C.border}` }}>
              <div style={{ fontFamily: F.sans, fontSize: 10, letterSpacing: 2, textTransform: "uppercase", color: C.muted }}>
                Notificaciones {noLeidas > 0 && <span style={{ color: "#EC4899" }}>· {noLeidas} nuevas</span>}
              </div>
              {noLeidas > 0 && (
                <button onClick={marcarTodasLeidas} style={{
                  fontFamily: F.sans, fontSize: 8, letterSpacing: 1, textTransform: "uppercase",
                  background: "transparent", border: "none", color: C.muted, cursor: "pointer",
                }}>Marcar todas leídas</button>
              )}
            </div>

            <div style={{ overflowY: "auto", flex: 1 }}>
              {notifs.length === 0 ? (
                <div style={{ padding: "24px 16px", fontFamily: F.sans, fontSize: 12, color: C.muted, textAlign: "center" }}>
                  Sin notificaciones
                </div>
              ) : notifs.map(n => {
                const tipo = NOTIF_TIPOS[n.tipo] || NOTIF_TIPOS.GENERAL;
                const fecha = n.creadoEn?.toDate?.() || new Date();
                return (
                  <div key={n.id}
                    onClick={() => marcarLeida(n.id)}
                    style={{
                      padding: "12px 16px", borderBottom: `0.5px solid ${C.border}`,
                      background: n.leida ? "transparent" : "#0D0D1A",
                      cursor: "pointer", transition: "background .15s",
                      display: "flex", gap: 10, alignItems: "flex-start",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = C.s2}
                    onMouseLeave={e => e.currentTarget.style.background = n.leida ? "transparent" : "#0D0D1A"}
                  >
                    <span style={{ fontSize: 14, flexShrink: 0, marginTop: 1 }}>{tipo.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: F.sans, fontSize: 11, color: n.leida ? C.off : C.white, marginBottom: 2, fontWeight: n.leida ? 400 : 500 }}>
                        {n.titulo}
                      </div>
                      <div style={{ fontFamily: F.sans, fontSize: 10, color: C.muted, lineHeight: 1.5 }}>{n.mensaje}</div>
                      <div style={{ fontFamily: F.mono, fontSize: 8, color: C.muted, marginTop: 4 }}>
                        {fecha.toLocaleDateString("es-AR")} {fecha.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                    {!n.leida && <div style={{ width: 5, height: 5, borderRadius: "50%", background: tipo.color, flexShrink: 0, marginTop: 4 }} />}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Admin: Panel de notificaciones ────────────────────────────────────────────
export function AdminNotificaciones() {
  const [usuarios, setUsuarios]   = useState([]);
  const [form, setForm]           = useState({ usuario_id: "todos", titulo: "", mensaje: "", tipo: "GENERAL" });
  const [enviando, setEnviando]   = useState(false);
  const [enviado, setEnviado]     = useState(false);
  const [historial, setHistorial] = useState([]);
  const [loadingH, setLoadingH]   = useState(true);

  useEffect(() => {
    const load = async () => {
      const [usersSnap, notifSnap] = await Promise.all([
        getDocs(collection(db, "usuarios")),
        getDocs(query(collection(db, "notificaciones"), orderBy("creadoEn", "desc"), limit(50))),
      ]);
      setUsuarios(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setHistorial(notifSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoadingH(false);
    };
    load();
  }, [enviado]);

  const enviar = async () => {
    if (!form.titulo || !form.mensaje) return;
    setEnviando(true);

    if (form.usuario_id === "todos") {
      // Enviar a todos los compradores
      const compradores = usuarios.filter(u => u.rol === "comprador");
      await Promise.all(compradores.map(u =>
        crearNotificacion({
          usuario_id: u.uid,
          usuario_email: u.email,
          tipo: form.tipo,
          titulo: form.titulo,
          mensaje: form.mensaje,
        })
      ));
    } else {
      const u = usuarios.find(x => x.id === form.usuario_id);
      await crearNotificacion({
        usuario_id: u?.uid || form.usuario_id,
        usuario_email: u?.email || "",
        tipo: form.tipo,
        titulo: form.titulo,
        mensaje: form.mensaje,
      });
    }

    setEnviando(false);
    setEnviado(true);
    setForm(p => ({ ...p, titulo: "", mensaje: "" }));
    setTimeout(() => setEnviado(false), 3000);
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "380px 1fr", gap: 20 }}>
      {/* Formulario */}
      <div style={{ background: C.s1, border: `0.5px solid ${C.border}`, padding: 24 }}>
        <div style={{ fontFamily: F.serif, fontSize: 20, color: C.white, marginBottom: 20 }}>
          Enviar notificación
        </div>

        <Label style={{ marginBottom: 6 }}>Destinatario</Label>
        <select value={form.usuario_id} onChange={e => setForm(p => ({ ...p, usuario_id: e.target.value }))}
          style={{ width: "100%", background: C.s2, border: `0.5px solid ${C.border2}`, color: C.off, fontFamily: F.sans, fontSize: 12, padding: "8px 10px", outline: "none", marginBottom: 18 }}>
          <option value="todos">Todos los compradores</option>
          {usuarios.filter(u => u.rol === "comprador").map(u => (
            <option key={u.id} value={u.id}>{u.nombre || u.email}</option>
          ))}
        </select>

        <Label style={{ marginBottom: 6 }}>Tipo</Label>
        <select value={form.tipo} onChange={e => setForm(p => ({ ...p, tipo: e.target.value }))}
          style={{ width: "100%", background: C.s2, border: `0.5px solid ${C.border2}`, color: C.off, fontFamily: F.sans, fontSize: 12, padding: "8px 10px", outline: "none", marginBottom: 18 }}>
          {Object.keys(NOTIF_TIPOS).map(k => (
            <option key={k} value={k}>{k.replace(/_/g," ")}</option>
          ))}
        </select>

        <Label style={{ marginBottom: 6 }}>Título</Label>
        <input value={form.titulo} onChange={e => setForm(p => ({ ...p, titulo: e.target.value }))}
          placeholder="Tu pedido fue embarcado"
          style={{ width: "100%", background: "transparent", border: "none", borderBottom: `0.5px solid ${C.border2}`, color: C.white, fontFamily: F.sans, fontSize: 13, padding: "7px 0", outline: "none", marginBottom: 18 }}
        />

        <Label style={{ marginBottom: 6 }}>Mensaje</Label>
        <textarea value={form.mensaje} onChange={e => setForm(p => ({ ...p, mensaje: e.target.value }))}
          placeholder="El contenedor zarpó el 15/06 y llega a Buenos Aires estimado el 20/08."
          rows={3}
          style={{ width: "100%", background: "transparent", border: `0.5px solid ${C.border2}`, color: C.white, fontFamily: F.sans, fontSize: 12, padding: 10, outline: "none", resize: "vertical", marginBottom: 20 }}
        />

        <Btn variant="solid" onClick={enviar} disabled={enviando || !form.titulo || !form.mensaje}
          style={{ width: "100%", justifyContent: "center" }}>
          {enviando ? "Enviando..." : enviado ? "✓ Enviado" : "Enviar notificación →"}
        </Btn>

        <div style={{ marginTop: 16, padding: "10px 14px", background: C.s2, border: `0.5px solid ${C.border}` }}>
          <Label style={{ marginBottom: 8 }}>Mensajes rápidos</Label>
          {[
            ["PEDIDO_PRODUCCION", "En producción", "Tu pedido ingresó a producción en fábrica. Fecha estimada de entrega al container: según ciclo activo."],
            ["PEDIDO_EMBARCADO",  "Embarcado",    "El contenedor zarpó. Te avisamos cuando llegue a Buenos Aires y esté disponible en depósito."],
            ["PEDIDO_DEPOSITO",   "Listo en depósito", "Tu mercadería está disponible en nuestro depósito en CABA. Coordiná la entrega por WhatsApp."],
          ].map(([tipo, titulo, mensaje]) => (
            <button key={tipo} onClick={() => setForm(p => ({ ...p, tipo, titulo, mensaje }))}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "6px 0", background: "transparent", border: "none", borderBottom: `0.5px solid ${C.border}`, color: C.off, fontFamily: F.sans, fontSize: 10, cursor: "pointer", letterSpacing: 0.5 }}>
              → {titulo}
            </button>
          ))}
        </div>
      </div>

      {/* Historial */}
      <div>
        <div style={{ fontFamily: F.serif, fontSize: 20, color: C.white, marginBottom: 16 }}>Historial</div>
        {loadingH ? <Spinner /> : (
          <div style={{ background: C.border }}>
            {historial.length === 0 && (
              <div style={{ background: C.s1, padding: 24, fontFamily: F.sans, fontSize: 12, color: C.muted, textAlign: "center" }}>
                Sin notificaciones enviadas.
              </div>
            )}
            {historial.map(n => {
              const tipo = NOTIF_TIPOS[n.tipo] || NOTIF_TIPOS.GENERAL;
              const fecha = n.creadoEn?.toDate?.() || new Date();
              return (
                <div key={n.id} style={{ background: C.s1, padding: "12px 16px", borderBottom: `0.5px solid ${C.border}`, display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <span style={{ fontSize: 13, flexShrink: 0 }}>{tipo.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                      <span style={{ fontFamily: F.sans, fontSize: 11, color: C.white }}>{n.titulo}</span>
                      <span style={{ fontFamily: F.mono, fontSize: 8, color: C.muted }}>
                        {fecha.toLocaleDateString("es-AR")}
                      </span>
                    </div>
                    <div style={{ fontFamily: F.sans, fontSize: 10, color: C.muted, lineHeight: 1.5 }}>{n.mensaje}</div>
                    <div style={{ fontFamily: F.mono, fontSize: 8, color: C.muted, marginTop: 4 }}>
                      Para: {n.usuario_email || "todos"} · {n.leida ? "Leída" : "No leída"}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
